/**
 * Client-side fraction detection for space-separated numbers
 * Detects patterns like "3 4" → "3/4" and "1 5 8" → "1-5/8"
 */

/**
 * Trade sizes halve: 1/2, 1/4, 1/8, 3/16, 5/32. A denominator that isn't a
 * power of two names a size no supplier stocks, so reading one out of a query
 * can only turn text the user typed into a term with nothing behind it.
 */
const FRACTION_DENOMINATORS = [2, 4, 8, 16, 32, 64];

function isFractionDenominator(num: number): boolean {
  return FRACTION_DENOMINATORS.includes(num);
}

function gcd(a: number, b: number): number {
  while (b) { [a, b] = [b, a % b]; }
  return a;
}

/** A span of the query and the text that replaces it. */
interface Replacement {
  start: number;
  end: number;
  replacement: string;
}

/**
 * The space-separated fractions in `query`, as spans into it. Indices are into
 * the query as given, whitespace and all.
 */
function collectSpaceSeparatedReplacements(query: string): Replacement[] {
  if (!query || typeof query !== "string") return [];

  // Preserve original whitespace - don't trim
  if (query.trim().length < 3) return []; // Need at least "3 4"

  // Pattern 1: "3 4" → "3/4" (simple fraction)
  // Match: two numbers separated by space, where second is common denominator
  const simpleFractionPattern = /\b(\d{1,2})\s+(\d{1,2})\b/g;
  const replacements: Replacement[] = [];

  // Find all potential simple fractions
  let match;
  while ((match = simpleFractionPattern.exec(query)) !== null) {
    const num1 = parseInt(match[1], 10);
    const num2 = parseInt(match[2], 10);

    // Safe interpretation rules:
    // - Both numbers ≤ 99
    // - Second number is common denominator
    // - First number < second number (valid fraction)
    if (
      num1 <= 99 &&
      num2 <= 99 &&
      isFractionDenominator(num2) &&
      num1 < num2 &&
      gcd(num1, num2) === 1
    ) {
      // Check if this is part of a mixed number pattern (e.g., "1 5 8")
      // Look ahead to see if there's another number
      const afterMatch = query.substring(match.index + match[0].length);
      const nextNumberMatch = afterMatch.match(/^\s+(\d{1,2})\b/);

      if (nextNumberMatch && num1 <= 9) {
        // If there's a third number and first number is single digit,
        // this could be a mixed number - skip this match, let mixed number handler catch it
        // The mixed number handler will validate if it's actually valid
        continue; // Skip, let mixed number pattern handle it
      }

      // Check if there's a large number (> 99) immediately before or after that suggests this isn't a fraction
      // Only skip if the large number is directly adjacent with no text in between (e.g., "100 5 8" but not "Model 1024 3 4")
      const beforeContext = query.substring(Math.max(0, match.index - 20), match.index);
      const afterContext = query.substring(match.index + match[0].length, match.index + match[0].length + 20);
      // Check if there's a large number immediately before (ending with space) or immediately after (starting with space)
      // But only skip if there's NO text (letters) between the large number and our match
      const hasLargeNumberBefore = /\b\d{3,}\s+$/.test(beforeContext);
      const hasTextBeforeLargeNumber = hasLargeNumberBefore && /[a-zA-Z]\s+\d{3,}\s+$/.test(beforeContext);
      const largeNumberDirectlyBefore = hasLargeNumberBefore && !hasTextBeforeLargeNumber;
      
      const hasLargeNumberAfter = /^\s+\d{3,}\b/.test(afterContext);
      const hasTextAfterLargeNumber = hasLargeNumberAfter && /^\s+\d{3,}\s+[a-zA-Z]/.test(afterContext);
      const largeNumberDirectlyAfter = hasLargeNumberAfter && !hasTextAfterLargeNumber;
      
      // Skip if there's a large number directly adjacent with no text in between (suggests model number pattern like "100 5 8")
      if (largeNumberDirectlyBefore || largeNumberDirectlyAfter) {
        continue;
      }

      // Valid simple fraction
      replacements.push({
        start: match.index,
        end: match.index + match[0].length,
        replacement: `${num1}/${num2}`,
      });
    }
  }

  // Pattern 2: "1 5 8" → "1-5/8" (mixed number)
  // Match: three numbers separated by spaces
  const mixedNumberPattern = /\b(\d{1})\s+(\d{1,2})\s+(\d{1,2})\b/g;
  const mixedReplacements: Replacement[] = [];

  // Reset regex
  simpleFractionPattern.lastIndex = 0;
  while ((match = mixedNumberPattern.exec(query)) !== null) {
    const whole = parseInt(match[1], 10);
    const num = parseInt(match[2], 10);
    const den = parseInt(match[3], 10);

    // Safe interpretation rules:
    // - All numbers ≤ 99
    // - Last number is common denominator
    // - Middle number < last number (valid fraction)
    // - First number is single digit (whole number part)
    // - Whole number must be > 0 (no "0-3/8")
    if (
      whole > 0 &&
      whole <= 9 &&
      num <= 99 &&
      den <= 99 &&
      isFractionDenominator(den) &&
      num < den &&
      gcd(num, den) === 1
    ) {
      // Check if this overlaps with a simple fraction replacement
      const overlaps = replacements.some(
        (r) =>
          match.index >= r.start &&
          match.index + match[0].length <= r.end,
      );

      if (!overlaps) {
        mixedReplacements.push({
          start: match.index,
          end: match.index + match[0].length,
          replacement: `${whole}-${num}/${den}`,
        });
      }
    }
  }

  return [...replacements, ...mixedReplacements];
}

/** Applies `replacements` to `text`, back to front so indices stay valid. */
function applyReplacements(text: string, replacements: Replacement[]): string {
  let result = text;
  for (const rep of [...replacements].sort((a, b) => b.start - a.start)) {
    result =
      result.substring(0, rep.start) +
      rep.replacement +
      result.substring(rep.end);
  }
  return result;
}

/** The digit runs in a query that read as fractions, as spans into it. */
interface NoSpaceMatches {
  twoDigit: Array<{ index: number; num1: number; num2: number }>;
  threeDigit: Array<{ index: number; whole: number; num: number; den: number }>;
}

function collectNoSpaceMatches(trimmed: string): NoSpaceMatches {
  
  // Pattern 1: Two digits anywhere like "34" → "3/4"
  // Find ALL two-digit patterns and pick the first valid fraction
  // Use lookbehind/lookahead to ensure digits are not part of larger numbers
  // Also ensure digits are not adjacent to letters (alphanumeric codes)
  const twoDigitPattern = /(?<!\d)(\d)(\d)(?!\d)/g;
  let twoDigitMatch;
  const twoDigitMatches: Array<{
    index: number;
    num1: number;
    num2: number;
  }> = [];
  
  while ((twoDigitMatch = twoDigitPattern.exec(trimmed)) !== null) {
    const num1 = parseInt(twoDigitMatch[1], 10);
    const num2 = parseInt(twoDigitMatch[2], 10);
    
    // Check if digits are adjacent to letters (alphanumeric codes should not match)
    const charBefore = twoDigitMatch.index > 0 ? trimmed[twoDigitMatch.index - 1] : '';
    const charAfter = twoDigitMatch.index + 2 < trimmed.length ? trimmed[twoDigitMatch.index + 2] : '';
    const isAdjacentToLetter = /[a-zA-Z]/.test(charBefore) || /[a-zA-Z]/.test(charAfter);
    
    // Check if followed by "degree" (angle measurement, not a fraction)
    const textAfter = trimmed.substring(twoDigitMatch.index + 2).trim();
    const isFollowedByDegree = /^\s*degree/i.test(textAfter);
    
    // Check if there's a large number (> 99) immediately before or after that suggests this isn't a fraction
    // Only skip if the large number is directly adjacent with no text in between (e.g., "100 34" but not "Model 1024 34")
    const beforeMatch = trimmed.substring(Math.max(0, twoDigitMatch.index - 20), twoDigitMatch.index);
    const afterMatch = trimmed.substring(twoDigitMatch.index + 2, twoDigitMatch.index + 2 + 20);
    // Check if there's a large number immediately before or after, but only skip if there's NO text between
    const hasLargeNumberBefore = /\b\d{3,}\s+$/.test(beforeMatch);
    const hasTextBeforeLargeNumber = hasLargeNumberBefore && /[a-zA-Z]\s+\d{3,}\s+$/.test(beforeMatch);
    const largeNumberBefore = hasLargeNumberBefore && !hasTextBeforeLargeNumber;
    
    const hasLargeNumberAfter = /^\s+\d{3,}\b/.test(afterMatch);
    const hasTextAfterLargeNumber = hasLargeNumberAfter && /^\s+\d{3,}\s+[a-zA-Z]/.test(afterMatch);
    const largeNumberAfter = hasLargeNumberAfter && !hasTextAfterLargeNumber;
    
    // Also check if there's a 2-digit number before that's larger than the potential fraction (e.g., "90 34" shouldn't match "34" as "3/4")
    // Only check 2-digit numbers, not 3+ digit numbers (those are handled by largeNumberBefore check)
    // Or if there's a number after that suggests it's not a fraction (e.g., "12 1" shouldn't match "12" as "1/2")
    const beforeNumberMatch = beforeMatch.match(/\b(\d{2})\s+$/);
    const beforeNumber = beforeNumberMatch ? parseInt(beforeNumberMatch[1], 10) : null;
    const shouldSkipDueToLargeBeforeNumber = beforeNumber !== null && beforeNumber > num2;
    
    const afterNumberMatch = afterMatch.match(/^\s+(\d+)\b/);
    const afterNumber = afterNumberMatch ? parseInt(afterNumberMatch[1], 10) : null;
    // Skip if there's a number after that's smaller (suggests it's not a fraction, e.g., "12 1")
    const shouldSkipDueToAfterNumber = afterNumber !== null && afterNumber < num2;
    
    // Check if this could be a fraction: first < second, second is common denominator
    // Don't match if adjacent to letters, followed by "degree", near large numbers, preceded by a larger number, or followed by a smaller number
    if (
      !isAdjacentToLetter &&
      !isFollowedByDegree &&
      !largeNumberBefore &&
      !largeNumberAfter &&
      !shouldSkipDueToLargeBeforeNumber &&
      !shouldSkipDueToAfterNumber &&
      num1 < num2 &&
      isFractionDenominator(num2) &&
      num1 > 0 &&
      gcd(num1, num2) === 1
    ) {
      twoDigitMatches.push({
        index: twoDigitMatch.index,
        num1,
        num2,
      });
    }
  }
  
  // Pattern 2: Three digits anywhere like "158" → "1-5/8"
  // Find ALL three-digit patterns and pick the first valid mixed number
  const threeDigitPattern = /(?<!\d)(\d)(\d)(\d)(?!\d)/g;
  let threeDigitMatch;
  const threeDigitMatches: Array<{
    index: number;
    whole: number;
    num: number;
    den: number;
  }> = [];
  
  while ((threeDigitMatch = threeDigitPattern.exec(trimmed)) !== null) {
    const whole = parseInt(threeDigitMatch[1], 10);
    const num = parseInt(threeDigitMatch[2], 10);
    const den = parseInt(threeDigitMatch[3], 10);
    
    // Check if digits are adjacent to letters (alphanumeric codes should not match)
    const charBefore = threeDigitMatch.index > 0 ? trimmed[threeDigitMatch.index - 1] : '';
    const charAfter = threeDigitMatch.index + 3 < trimmed.length ? trimmed[threeDigitMatch.index + 3] : '';
    const isAdjacentToLetter = /[a-zA-Z]/.test(charBefore) || /[a-zA-Z]/.test(charAfter);
    
    // Check if followed by "degree" (angle measurement, not a fraction)
    const textAfter = trimmed.substring(threeDigitMatch.index + 3).trim();
    const isFollowedByDegree = /^\s*degree/i.test(textAfter);
    
    // Check if there's a large number (> 99) immediately before or after that suggests this isn't a fraction
    // Only skip if the large number is directly adjacent with no text in between (e.g., "100 158" but not "Model 1024 158")
    const beforeMatch = trimmed.substring(Math.max(0, threeDigitMatch.index - 20), threeDigitMatch.index);
    const afterMatch = trimmed.substring(threeDigitMatch.index + 3, threeDigitMatch.index + 3 + 20);
    // Check if there's a large number immediately before or after, but only skip if there's NO text between
    const hasLargeNumberBefore = /\b\d{3,}\s+$/.test(beforeMatch);
    const hasTextBeforeLargeNumber = hasLargeNumberBefore && /[a-zA-Z]\s+\d{3,}\s+$/.test(beforeMatch);
    const largeNumberBefore = hasLargeNumberBefore && !hasTextBeforeLargeNumber;
    
    const hasLargeNumberAfter = /^\s+\d{3,}\b/.test(afterMatch);
    const hasTextAfterLargeNumber = hasLargeNumberAfter && /^\s+\d{3,}\s+[a-zA-Z]/.test(afterMatch);
    const largeNumberAfter = hasLargeNumberAfter && !hasTextAfterLargeNumber;
    
    // Check if this could be a mixed number:
    // - First digit is single digit (whole number)
    // - Middle < last (valid fraction)
    // - Last is common denominator
    // - Whole number must be > 0 (no "0-3/8")
    // - Don't match if adjacent to letters, followed by "degree", or near large numbers
    if (
      !isAdjacentToLetter &&
      !isFollowedByDegree &&
      !largeNumberBefore &&
      !largeNumberAfter &&
      whole > 0 &&
      whole <= 9 &&
      num < den &&
      isFractionDenominator(den) &&
      num > 0 &&
      gcd(num, den) === 1
    ) {
      threeDigitMatches.push({
        index: threeDigitMatch.index,
        whole,
        num,
        den,
      });
    }
  }
  
  return { twoDigit: twoDigitMatches, threeDigit: threeDigitMatches };
}

/** The no-space readings of a query, as replacement spans. */
function noSpaceReplacements(matches: NoSpaceMatches): Replacement[] {
  return [
    ...matches.twoDigit.map((m) => ({
      start: m.index,
      end: m.index + 2,
      replacement: `${m.num1}/${m.num2}`,
    })),
    ...matches.threeDigit.map((m) => ({
      start: m.index,
      end: m.index + 3,
      replacement: `${m.whole}-${m.num}/${m.den}`,
    })),
  ];
}

/** One piece of fraction shorthand, and where it sits in the query. */
export interface FractionRewrite {
  /** Where the shorthand starts, as an index into the trimmed query. */
  index: number;
  /** How many characters of the query it covers. */
  length: number;
  /** What replaces them, e.g. "1/2" or "1-5/8". */
  text: string;
}

/**
 * Every piece of fraction shorthand in `query`, reported separately with its
 * position rather than pre-applied, so a caller can take some and leave
 * others — a search box holds back the one sitting on the word the user is
 * still typing, and applies the rest.
 *
 * Indices are into the returned `trimmed` text, which is what the rewrites
 * should be applied to.
 */
export function detectFractionRewrites(query: string): {
  trimmed: string;
  rewrites: FractionRewrite[];
} {
  const trimmed = typeof query === "string" ? query.trim() : "";
  if (!trimmed) return { trimmed, rewrites: [] };

  const accepted = noSpaceReplacements(collectNoSpaceMatches(trimmed));

  // A no-space reading wins any overlap: it is the more specific reading of a
  // digit run, and it is the one this has always tried first.
  for (const candidate of collectSpaceSeparatedReplacements(trimmed)) {
    const overlaps = accepted.some(
      (a) => candidate.start < a.end && a.start < candidate.end,
    );
    if (!overlaps) accepted.push(candidate);
  }

  return {
    trimmed,
    rewrites: accepted
      .sort((a, b) => a.start - b.start)
      .map((r) => ({
        index: r.start,
        length: r.end - r.start,
        text: r.replacement,
      })),
  };
}

/** Applies `rewrites` to the text they were found in. */
export function applyFractionRewrites(
  text: string,
  rewrites: FractionRewrite[],
): string {
  return applyReplacements(
    text,
    rewrites.map((r) => ({
      start: r.index,
      end: r.index + r.length,
      replacement: r.text,
    })),
  );
}
